import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { CheckCircle2, Star, Home, Users, ShieldCheck, ArrowRight, Search, MapPin, Calendar, User, Quote, Phone, Mail } from 'lucide-react';
import { cn } from '../utils/cn';
export function HomePage() {
  return <div className="w-full" data-id="element-274">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 pb-32 overflow-hidden" data-id="element-275">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-secondary/5 -z-10 rounded-bl-[200px]" data-id="element-276" />
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-accent/5 blur-[120px] rounded-full -z-10" data-id="element-277" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-278">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="space-y-8" data-id="element-279">
            <div className="inline-flex items-center space-x-2 bg-primary/5 text-primary px-4 py-2 rounded-full text-sm font-bold tracking-wide uppercase" data-id="element-280">
              <ShieldCheck size={16} data-id="element-281" />
              <span data-id="element-282">Serving Communities Across Maryland</span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-display font-extrabold text-charcoal leading-[1.1] text-balance" data-id="element-283">
              A Safe Place to <span className="text-primary" data-id="element-284">Call Home</span>
            </h1>

            <p className="text-xl text-muted leading-relaxed max-w-xl" data-id="element-285">
              Providing supportive independent living opportunities for adults
              throughout Maryland in a comfortable, community-focused
              environment.
            </p>

            <div className="flex flex-wrap gap-4" data-id="element-286">
              <Button size="lg" variant="primary" data-id="element-287">
                Check Availability
              </Button>
              <Button size="lg" variant="outline" data-id="element-288">
                Schedule a Tour
              </Button>
              <Button size="lg" variant="ghost" className="group" data-id="element-289">
                Make a Referral{' '}
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-290" />
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4" data-id="element-291">
              {['Safe Housing', 'Community Focused', 'Referral Friendly', 'Serving Maryland'].map(item => <div key={item} className="flex items-center space-x-2 text-sm font-semibold text-charcoal/70" data-id="element-292">
                  <CheckCircle2 size={16} className="text-accent" data-id="element-293" />
                  <span data-id="element-294">{item}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Content - Overlapping Imagery */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative" data-id="element-295">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] lg:aspect-square" data-id="element-296">
              <img src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&q=80&w=1000" alt="Modern Residential Home" className="w-full h-full object-cover" data-id="element-297" />
            </div>

            {/* Floating Cards */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-10 -left-10 z-20 bg-white p-6 rounded-2xl shadow-premium hidden md:block" data-id="element-298">
              <div className="flex items-center space-x-4" data-id="element-299">
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center text-accent" data-id="element-300">
                  <Home size={24} data-id="element-301" />
                </div>
                <div data-id="element-302">
                  <div className="text-2xl font-bold text-charcoal" data-id="element-303">15+</div>
                  <div className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-304">
                    Homes Statewide
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute top-1/2 -right-1 z-20 bg-primary text-white p-6 rounded-2xl shadow-premium hidden md:block" data-id="element-305">
              <div className="flex items-center space-x-4" data-id="element-306">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-307">
                  <Users size={24} data-id="element-308" />
                </div>
                <div data-id="element-309">
                  <div className="text-2xl font-bold" data-id="element-310">100+</div>
                  <div className="text-xs font-bold text-white/70 uppercase tracking-wider" data-id="element-311">
                    Residents Supported
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            x: [0, 5, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} className="absolute -bottom-8 left-1/4 z-20 bg-white p-4 rounded-xl shadow-premium border border-accent/20" data-id="element-312">
              <div className="flex items-center space-x-2" data-id="element-313">
                <div className="flex -space-x-2" data-id="element-314">
                  {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-315">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Resident" data-id="element-316" />
                    </div>)}
                </div>
                <div className="text-sm font-bold text-charcoal" data-id="element-317">
                  <div className="flex text-accent" data-id="element-318">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={12} fill="currentColor" data-id="element-319" />)}
                  </div>
                  Beds Available Today
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Search Section */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="relative z-30 -mt-16 px-6" data-id="element-320">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-321">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end" data-id="element-322">
            <div className="space-y-2" data-id="element-323">
              <label className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-324">
                County
              </label>
              <div className="relative" data-id="element-325">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-326" />
                <select className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary appearance-none" data-id="element-327">
                  <option data-id="element-328">All Counties</option>
                  <option data-id="element-329">Baltimore City</option>
                  <option data-id="element-330">Baltimore County</option>
                  <option data-id="element-331">Montgomery County</option>
                </select>
              </div>
            </div>
            <div className="space-y-2" data-id="element-332">
              <label className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-333">
                Gender
              </label>
              <div className="relative" data-id="element-334">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-335" />
                <select className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary appearance-none" data-id="element-336">
                  <option data-id="element-337">Any Gender</option>
                  <option data-id="element-338">Male</option>
                  <option data-id="element-339">Female</option>
                </select>
              </div>
            </div>
            <div className="space-y-2" data-id="element-340">
              <label className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-341">
                Move-in Date
              </label>
              <div className="relative" data-id="element-342">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-343" />
                <input type="date" className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-344" />
              </div>
            </div>
            <Button className="w-full py-4 flex items-center justify-center space-x-2" data-id="element-345">
              <Search size={20} data-id="element-346" />
              <span data-id="element-347">Search Availability</span>
            </Button>
          </div>
        </div>
      </motion.section>

      {/* Welcome Section */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-32 px-6" data-id="element-348">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-349">
          <div className="relative" data-id="element-350">
            <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-351">
              <img src="https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=1000" alt="Bright Modern Living Space" className="w-full aspect-[4/3] object-cover" data-id="element-352" />
            </div>
            <div className="absolute -bottom-10 -right-10 bg-accent p-10 rounded-2xl hidden md:block max-w-xs" data-id="element-353">
              <Quote className="text-white/40 mb-4" size={40} data-id="element-354" />
              <p className="text-white font-medium italic leading-relaxed" data-id="element-355">
                "We don't just provide a room; we provide a community where
                every resident feels valued and safe."
              </p>
            </div>
          </div>

          <div className="space-y-8" data-id="element-356">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-charcoal leading-tight" data-id="element-357">
              Welcome to Birds Nest Independent Living
            </h2>
            <div className="space-y-6 text-lg text-muted leading-relaxed" data-id="element-358">
              <p data-id="element-359">
                At Birds Nest, we believe that everyone deserves a safe, stable,
                and supportive place to call home. Our mission is to bridge the
                gap between healthcare and housing by providing premium
                independent living accommodations for adults throughout
                Maryland.
              </p>
              <p data-id="element-360">
                Our homes are more than just residences—they are vibrant
                communities designed to foster independence while providing the
                safety net of professional management and 24/7 support access.
              </p>
            </div>
            <div className="pt-4" data-id="element-361">
              <Button variant="outline" size="lg" data-id="element-362">
                Learn More About Our Mission
              </Button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Services Preview */}
      <motion.section initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="py-32 bg-primary px-6 relative overflow-hidden" data-id="element-363">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" data-id="element-364">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-[100px]" data-id="element-365" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent rounded-full blur-[100px]" data-id="element-366" />
        </div>

        <div className="max-w-7xl mx-auto text-center mb-20" data-id="element-367">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6" data-id="element-368">
            Our Core Services
          </h2>
          <p className="text-white/70 text-xl max-w-2xl mx-auto" data-id="element-369">
            Comprehensive housing solutions tailored to the unique needs of our
            residents and referral partners.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-370">
          {[{
          title: 'Independent Living',
          icon: Home,
          desc: 'High-quality shared housing with all utilities included and fully furnished spaces.'
        }, {
          title: 'Safe Community',
          icon: ShieldCheck,
          desc: 'Secure environments with 24/7 emergency support and professional property management.'
        }, {
          title: 'Housing Placement',
          icon: MapPin,
          desc: 'Rapid placement services for individuals transitioning from hospitals or treatment centers.'
        }, {
          title: 'Community Resources',
          icon: Users,
          desc: 'Connections to local healthcare, transportation, and social services to support independence.'
        }].map((service, idx) => <motion.div key={service.title} whileHover={{
          y: -10
        }} className="bg-white/10 backdrop-blur-md border border-white/10 p-8 rounded-2xl group transition-all duration-300 flex flex-col h-full" data-id="element-371">
              <div className="w-14 h-14 bg-accent rounded-xl flex items-center justify-center text-charcoal mb-6 group-hover:scale-110 transition-transform shrink-0" data-id="element-372">
                <service.icon size={28} data-id="element-373" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-374">
                {service.title}
              </h3>
              <p className="text-white/60 mb-6 leading-relaxed" data-id="element-375">
                {service.desc}
              </p>
              <Link to="/services" className="inline-flex items-center text-accent font-bold hover:underline mt-auto" data-id="element-376">
                Learn More <ArrowRight size={16} className="ml-2" data-id="element-377" />
              </Link>
            </motion.div>)}
        </div>
      </motion.section>

      {/* Featured Properties */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-32 px-6 bg-background" data-id="element-378">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-end mb-16 gap-8" data-id="element-379">
          <div className="max-w-2xl" data-id="element-380">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-charcoal mb-6" data-id="element-381">
              Featured Properties
            </h2>
            <p className="text-muted text-lg" data-id="element-382">
              Explore our modern, comfortable homes across Maryland. Each
              location is carefully selected for safety and accessibility.
            </p>
          </div>
          <Button variant="outline" data-id="element-383">View All Properties</Button>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-384">
          {[{
          name: 'The Silver Spring Nest',
          city: 'Silver Spring',
          county: 'Montgomery',
          beds: '2 Available',
          status: 'Available',
          img: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&q=80&w=800'
        }, {
          name: 'Baltimore Harbor View',
          city: 'Baltimore',
          county: 'Baltimore City',
          beds: 'Limited',
          status: 'Limited',
          img: 'https://images.unsplash.com/photo-1576941089067-2de3c901e126?auto=format&fit=crop&q=80&w=800'
        }, {
          name: 'Towson Community Home',
          city: 'Towson',
          county: 'Baltimore County',
          beds: 'Waitlist',
          status: 'Waitlist',
          img: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800'
        }].map(property => <div key={property.name} className="bg-white rounded-2xl overflow-hidden shadow-premium group hover:shadow-premium-hover transition-all duration-300 border border-gray-100" data-id="element-385">
              <div className="relative h-64 overflow-hidden" data-id="element-386">
                <img src={property.img} alt={property.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-387" />
                <div className={cn('absolute top-4 right-4 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider', property.status === 'Available' ? 'bg-green-500 text-white' : property.status === 'Limited' ? 'bg-accent text-charcoal' : 'bg-gray-500 text-white')} data-id="element-388">
                  {property.status}
                </div>
              </div>
              <div className="p-8" data-id="element-389">
                <div className="text-xs font-bold text-accent uppercase tracking-widest mb-2" data-id="element-390">
                  {property.county} County
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-4" data-id="element-391">
                  {property.name}
                </h3>
                <div className="flex items-center text-muted mb-6 space-x-4 text-sm" data-id="element-392">
                  <div className="flex items-center" data-id="element-393">
                    <MapPin size={16} className="mr-1" data-id="element-394" /> {property.city}
                  </div>
                  <div className="flex items-center" data-id="element-395">
                    <Users size={16} className="mr-1" data-id="element-396" /> {property.beds}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-397">
                  <Button variant="primary" size="sm" data-id="element-398">
                    Apply Now
                  </Button>
                  <Button variant="outline" size="sm" data-id="element-399">
                    View Details
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </motion.section>

      {/* Referral Partners */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-32 px-6 bg-primary text-white" data-id="element-400">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-401">
          <div className="space-y-8" data-id="element-402">
            <h2 className="text-4xl lg:text-5xl font-display font-bold leading-tight" data-id="element-403">
              Trusted Partner for Referral Agencies
            </h2>
            <p className="text-white/70 text-xl leading-relaxed" data-id="element-404">
              We work closely with hospitals, case managers, and social service
              organizations to provide seamless housing transitions for their
              clients.
            </p>
            <div className="space-y-4" data-id="element-405">
              {['Fast placement turnarounds', 'Transparent availability reporting', 'Comprehensive move-in coordination', 'Regular resident progress updates'].map(item => <div key={item} className="flex items-center space-x-3" data-id="element-406">
                  <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-charcoal" data-id="element-407">
                    <CheckCircle2 size={14} data-id="element-408" />
                  </div>
                  <span className="font-medium" data-id="element-409">{item}</span>
                </div>)}
            </div>
            <div className="pt-6" data-id="element-410">
              <Button variant="accent" size="lg" data-id="element-411">
                Submit a Referral
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6" data-id="element-412">
            <div className="space-y-6" data-id="element-413">
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-414">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-415">24h</h4>
                <p className="text-white/60 font-medium" data-id="element-416">Response Time</p>
              </div>
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-417">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-418">50+</h4>
                <p className="text-white/60 font-medium" data-id="element-419">Active Partners</p>
              </div>
            </div>
            <div className="space-y-6 mt-12" data-id="element-420">
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-421">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-422">100%</h4>
                <p className="text-white/60 font-medium" data-id="element-423">Compliance</p>
              </div>
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-424">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-425">MD</h4>
                <p className="text-white/60 font-medium" data-id="element-426">Statewide Coverage</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Inquiry Form */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-32 px-6" data-id="element-427">
        <div className="max-w-7xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row" data-id="element-428">
          <div className="lg:w-1/2 p-12 lg:p-20 bg-secondary/5" data-id="element-429">
            <h2 className="text-4xl font-display font-bold text-charcoal mb-8" data-id="element-430">
              Ready to find a safe place to call home?
            </h2>
            <p className="text-muted text-lg mb-12" data-id="element-431">
              Fill out the form below and our admissions team will contact you
              within 24 hours to discuss availability and schedule a tour.
            </p>

            <div className="space-y-8" data-id="element-432">
              <div className="flex items-start space-x-4" data-id="element-433">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-primary shrink-0" data-id="element-434">
                  <Phone size={24} data-id="element-435" />
                </div>
                <div data-id="element-436">
                  <h4 className="font-bold text-charcoal" data-id="element-437">Call Us Directly</h4>
                  <p className="text-muted" data-id="element-438">(410) 555-0123</p>
                </div>
              </div>
              <div className="flex items-start space-x-4" data-id="element-439">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-primary shrink-0" data-id="element-440">
                  <Mail size={24} data-id="element-441" />
                </div>
                <div data-id="element-442">
                  <h4 className="font-bold text-charcoal" data-id="element-443">Email Admissions</h4>
                  <p className="text-muted" data-id="element-444">admissions@birdsnestliving.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2 p-12 lg:p-20" data-id="element-445">
            <form className="space-y-6" data-id="element-446">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-447">
                <div className="space-y-2" data-id="element-448">
                  <label className="text-sm font-bold text-charcoal" data-id="element-449">
                    Full Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="John Doe" data-id="element-450" />
                </div>
                <div className="space-y-2" data-id="element-451">
                  <label className="text-sm font-bold text-charcoal" data-id="element-452">
                    Phone Number
                  </label>
                  <input type="tel" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="(410) 000-0000" data-id="element-453" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-454">
                <label className="text-sm font-bold text-charcoal" data-id="element-455">
                  Email Address
                </label>
                <input type="email" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="john@example.com" data-id="element-456" />
              </div>
              <div className="space-y-2" data-id="element-457">
                <label className="text-sm font-bold text-charcoal" data-id="element-458">
                  Interested In
                </label>
                <select className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary appearance-none" data-id="element-459">
                  <option data-id="element-460">Independent Living</option>
                  <option data-id="element-461">Referral Partnership</option>
                  <option data-id="element-462">General Inquiry</option>
                </select>
              </div>
              <div className="space-y-2" data-id="element-463">
                <label className="text-sm font-bold text-charcoal" data-id="element-464">
                  Message
                </label>
                <textarea rows={4} className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="How can we help you?" data-id="element-465"></textarea>
              </div>
              <Button className="w-full py-4 text-lg" data-id="element-466">Send Inquiry</Button>
            </form>
          </div>
        </div>
      </motion.section>
    </div>;
}